/**
 * @module CIApp
 */
import Message from "./model/Message";
import Properties from "./Properties";
import GetMessageHandler from "./GetMessageHandler";
import { functionToPromiseWrapper } from "./util/PromiseWrapper";
import {
    CALL_STATE,
    CHAT_STATE,
    Constants,
    EMAIL_STATE,
    KEY_TYPE,
    MessageType,
    NEW_PAGE_MINIMIZED,
    RICH_WIDGET,
    STATUS_STATE,
    SYSTEM_STATE,
    TOGGLE_SOUND,
    FOCUS_ON_OPEN,
    WILD_CARDS
} from "./model/Constants";
import UIRender from "./ui/UIRender";
import BubbleType from "./model/BubbleType";
import ShoutUIController from "./ui/ShoutUi";

// import "./ui/css/chatui.css";

import {isObject, Timeout, decodeEntities, stripHeaderFromTranscript } from "./util/Misc";
import SndMgr from "./util/SndMgr";
import StateMgr from "./util/StateMgr";
import { RichMediaReporting } from "./ui/bubbleviews/controller/RichMediaReporting";
import pubSubGlobal from "./util/pubSubGlobal";
import FocusMgr from "./util/FocusMgr";
import { MessageHandler } from "../model";

/* eslint-disable no-param-reassign */
/**
 * Object which represent an image resourcee
 * @typedef {Object<string,any>} ImageO
 * @property {string} ImageO.imgname - name of the image which is stored under nuance theme folder
 * @param {string} ImageO.imgsrc - url pointing to an image file
 */

/**
 * WebSDK Messaging UI functionality is build into the CIApp class,
 * CIApp class is the starting point for all UI rendering in the messaging UI
 * @class {CIApp}
 * @extends {Properties}
 *
 */
export default class CIApp extends Properties {
    bridge: any;

    uiConfig: any;

    shoutui: any;

    sndMgr: any;

    focusMgr: any;

    recentConversationID: any;

    engagementID: any;

    conversationID: any;

    agentID: any;

    asyncPrev: boolean;

    isRestoring: boolean;

    isLastConvActive: boolean;

    isAsyncChat: any;

    isAsyncReconnect: boolean;

    asyncMode: any;

    showOpenerForEndedConv: any;

    stateMgr: any;

    renderEngine: any;

    isChatDone: boolean;

    getMessageApi: MessageHandler;

    openerTimers: any;

    clientOutCome: boolean;

    openerDisplayIndex: number;

    lastHasMessages: boolean;

    conversations: any;

    conIndex: number;

    lazyloading: boolean;

    richReporter: any;

    sdkInst: any;
    /**
     * @constructor
     * @param {FrameBridge|MessagingApp} bridge
     */

    constructor(bridge) {
        super();

        /**
         * @type {FrameBridge|MessagingApp} instance
         */
        this.bridge = bridge;

        /**
         * @type {Object} ui config json object
         */
        this.uiConfig = bridge.getUIConfig();

        /**
         * @type {SDKInstance} SDKInstance interface
         *
         * Note: This is a reference that links back to
         * FlashPeer.js => getSDKInstance()
         */
        this.sdkInst = bridge.getSDKInstance();

        /**
         * @type {ShoutUIController}
         */
        this.shoutui = new ShoutUIController(this);
        this.onUiConfigLoaded();
        this.shoutui.initializeComponents();

        /**
         *
         * @type {SndMgr} sndMgr
         */
        this.sndMgr = new SndMgr(this, this.uiConfig.appConfigProps.audioSource);

        /**
         * @type {FocusMgr} focusMgr
         */
        this.focusMgr = new FocusMgr(this);

        /**
         * @type {number} conversation id of the most recent async chat conversation
         */
        this.recentConversationID = null;

        /**
         * @type {number} engagement id of current chat
         */
        this.engagementID = null;

        /**
         * @type {number} conversation id of async conversation
         */
        this.conversationID = null;

        /**
         * @type {number} agent id of current engagement
         */
        this.agentID = null
    }

    /**
     * Life Cycle method of CIApp class that kick-in the creation of Chat Layout
     */
    onCreateFrame() {
        this.asyncPrev = false;
        this.isRestoring = false;
        this.isLastConvActive = false;
        this.isAsyncChat = this.sdkInst.isAsyncChat();
        this.isAsyncReconnect = false;
        this.asyncMode = this.sdkInst.getAsyncChatMode();
        this.showOpenerForEndedConv = this.sdkInst.showOpenerForEndedConv();

        // this.application = Application.application;
        this.isRestoring = this.sdkInst.isChatInProgress();

        /**
         * App state manager
         * @property {string} state - Application state (STARTING, ENAGAGED...)
         * @property {integer} emailState - email requested state
         * @property {Object} survey - Post-chat or Pre-chat survey state object
         * @property {Boolean} isMinimized - application is minimized or not
         * @property {Object} FrameBridge - Holds states coming from framebridge
         * @property {String} ariaReaderSay - Set values are read by the
         *                                  ariaReaderView(subscribed to this)
         * @property {Boolean} isCloseConfirmVisible - Modal Visibility
         */
        this.stateMgr = new StateMgr({
            state: this.isRestoring ? CHAT_STATE.ENGAGED : CHAT_STATE.STARTING,
            emailState: this.isEmailRequested() ? EMAIL_STATE.REQUESTED : EMAIL_STATE.DISABLED,
            survey: {
                visible: false,
                domain: "",
                evt: "",
                automatonType: "",
            },
            isMinimized: false,
            FrameBridge: {
                readyFocus: "",
                setCount: 0,
            },
            ariaReaderSay: "",
            isCloseConfirmVisible: false,
            isModalVisible: false,
        });

        this.shoutui.kickInLayout();
        this.renderEngine = new UIRender(this);
        this.isChatDone = false;

        this.setOpenerAndReadyFrame();
        this.getMessageApi = new GetMessageHandler(this);

        if (this.sdkInst.checkIfMinimized().state === true) {
            this.getStateMgr().setState({ isMinimized: true });
        }

        // If pcsID(Pre Chat Survey ID) is present then display FAB buttons
        if (this.uiConfig.pcsID && window.c2cPlugin?.displayChatFab) {
            window.c2cPlugin.displayChatFab();
        }

        window.RichMain.setup({ skinPath: this.uiConfig.skinPath });
    }

    /**
     * This method is called when we close the chat and click on c2c again to
     * open the chat back up. This is called instead of the CiApp constructor,
     * because we don't delete the ciApp instance.
     */
    onReinitFrame(bridge) {
        this.bridge = bridge;
        this.uiConfig = bridge.getUIConfig();
        this.onUiConfigLoaded();
        this.sndMgr = new SndMgr(this, this.uiConfig.appConfigProps.audioSource);
    }

    /**
     * Perform any modifications required for uiConfig once it's loaded
     *
     * This is going to be called when CiApp is first initialized (from the
     * CiApp constructor), but also called from `onReinitFrame` when we close
     * the chat, and click on c2c again.
     */
    private onUiConfigLoaded(): void {
        super.initAppStringProperties(this.uiConfig.appStringProps);
        super.initConfigProperties(this.uiConfig.appConfigProps);
        super.filterHiddenMenu(this.uiConfig);
        super.filterSurveyItem(this.uiConfig.topBarConfig.topBarSettings, this.uiConfig.footerConfig.footerSettings);
    }

    /**
     * Life Cycle method of CIApp class that handles minimized event
     */
    onMinimizeFrame() {
        this.bridge.minimizeFrame();
        this.sdkInst.sendActivityMessage(this.sdkInst.ACTIVITY_STATE.MINIMIZED);

        this.getStateMgr().setState({ isMinimized: true });
    }

    /**
     * Life Cycle method of CIApp class that handles restore event
     */
    onResumeFrame() {
        this.sdkInst.sendActivityMessage("restore");

        this.getStateMgr().setState({ isMinimized: false });
        this.focusMgr.whenFrameRestored();
    }

    /**
     * Life Cycle method of CIApp class that handles destroy event
     */
    onDestroyFrame() {
        // clear if opener timer if any
        if (this.openerTimers && this.openerTimers.length > 0) {
            this.openerTimers.forEach(timer => {
                timer.clear();
            });
        }

        this.openerText = null;
        this.focusMgr.clearRestoreFocusTimeout();
        this.shoutui.clear();
    }

    /**
     * getting the opener or getting previous conversation transcript in case of aync chat
     */
    setOpenerAndReadyFrame() {
        if (this.sdkInst.isOnlyPostChatSurvey()) {
            this.displayPostChatSurveyUponChatload();
        } else if (this.isAsyncChat) {
            this.getPrevConversation(false);
        } else {
            this.shoutui.getLayoutEl().renderSurvey().then(engageParams => {
                if (engageParams) {
                    this.setupPreChatParams(engageParams);
                } else if (this.isRestoring && this.getPersistedValue(Constants.CLIENT_OUT, -1) === 1) {
                    this.clientOutCome = true;
                }

                this.continueChatRendering().then(() => {
                    if (engageParams) {
                        Timeout.instantiate(() => {
                            this.autoEngage(engageParams.agentOutcome, engageParams.data);
                        }, 250);
                    } else if (this.startAutoEngagement && !this.isRestoring) {
                        this.autoEngage(this.autoEngageText);
                    }
                });
            });
        }
    }



    continueChatRendering() {
        this.setDisclaimerText();
        return this.getOpener(true);
    }

    isPreChatSurveyNeed() {
        if (!!this.uiConfig.displayPreChatSurvey && !this.isRestoring) {
            this.stateMgr.setState({ state: CHAT_STATE.PRECHAT });
            return true;
        }
        return false;
    }

    setupPreChatParams(params) {
        if (params.datapass) {
            params.agentOutcome = "";
            Object.keys(params.datapass).forEach(key => {
                params.agentOutcome += [key, ":", params.datapass[key], "\n"].join("");
            });

            if (params.datapass.customerName) {
                this.customerName = params.datapass.customerName;
            }
        }

        if (params.data && params.data.clientOutcome) {
            this.openerText = params.data.clientOutcome;
            this.clientOutCome = true;
            this.persistValue(Constants.CLIENT_OUT, 1);
        }
    }

    setDisclaimerText(restore = false) {
        if (this.showDisclaimerText && this.disclaimerText) {
            const msg = new Message({
                bubbleType: BubbleType.DISCLAIMER_TEXT,
                messageText: this.disclaimerText,
            });
            msg.setRestore(restore);
            this.addToUI(msg);
        }
    }

    /**
     * Retrieves opener script by making a call to SDKInstance
     *
     * @param {boolean} add - determines if the opener script must be added
     * to the chat window, if false it will only be set as part of engage params
     *
     * @return {Promise<any>}
     */
    getOpener(add) {
        let openerTS;

        return functionToPromiseWrapper(cb => {
            /**
             * use the opener script provided by client in UIConfig object
             */
            if ((this.getRestoring() && this.clientOutCome) || this.isChatEngaged()) {
                cb([]);
            } else if (this.openerText) {
                if (this.openerText.startsWith(Constants.EA)) {
                    cb([]);
                    // When the opener is empty then shift focus on input
                    this.focusMgr.handleFocusWhenChatLaunched(null, FOCUS_ON_OPEN.ON_INPUT);
                } else {
                    cb(Array.isArray(this.openerText) || [this.openerText]);
                }
            } else {
                this.sdkInst.getOpenerScripts(openers => {
                    cb(openers);
                });
            }
        }).then((openers: any) => {
            if (this.isChatEngaged()) {
                return null;
            }

            if (this.getRestoring()) {
                openerTS = parseInt(this.getPersistedValue(Constants.OPENERTS, Date.now()), 10);

                const displayCount = parseInt(this.getPersistedValue(Constants.OPENERDISPLAY, 0), 10);

                if (displayCount != 0 && openers.length > 0) {
                    openers = openers.splice(0, displayCount);
                }
            } else {
                openerTS = Date.now();
            }


            if (add) {
                this.openerTimers = [];
                this.openerDisplayIndex = 0;

                if (openers.length == 0) {
                    if (this.uiConfig.appConfigProps.showTypingInOpener) {
                        this.showTypingIndicator(this.typingMsg);
                    } else if (!this.clientOutCome && !this.openerText) {
                        const emptyOpener = this.uiConfig.appStringProps.emptyOpener || "Hello";
                        openers.push(emptyOpener);
                    }
                }
                openers = openers.map(opener => this.replaceWildcardInOpener(opener));
                openers.forEach((opener, index) => {
                    if (this.getRestoring()) {
                        this.renderOpenerMessage(opener, openerTS, true);
                    } else {
                        /**
                         * delay loading of subsequent openers if there is more than one, delay can be controlled a UIConfig property
                         */
                        this.openerTimers.push(Timeout.instantiate(() => {
                            this.renderOpenerMessage(opener, openerTS + (index * this.openerDelay), false);

                            if (!this.clientOutCome) {
                                this.sdkInst.setOpeners([decodeEntities(opener)], this.agentAlias);
                            }

                            this.openerDisplayIndex += 1;
                        }, index * this.openerDelay));
                    }
                });
            } else if (!this.getRestoring() && !this.clientOutCome) {
                openers = openers.map(op => decodeEntities(op));
                this.sdkInst.setOpeners(openers, this.agentAlias);
            }

            return openers;
        }).then(openers => { // eslint-disable-line no-unused-vars
            if (!this.isAsyncChat) {
                this.onFrameViewReady(false);
            }
        });
    }

    replaceWildcardInOpener(opener) {
        // check if opener contains the pattern "%CUSTOMER_NAME". if so, replace this with the customer name
        if (typeof opener === 'string' && opener.toLowerCase().indexOf(WILD_CARDS.CUSTOMER_NAME.toLowerCase()) > -1) {
            const patternRegex = new RegExp(WILD_CARDS.CUSTOMER_NAME, 'i');
            const name = this.customerName ? this.customerName : "";
            return opener.replace(patternRegex, name);
        }
        return opener;
    }

    /**
     *
     * @param {String} opener
     * @param {number} openerts
     * @param {Boolean} isRestoring
     */
    renderOpenerMessage(opener, openerts, isRestoring) {
        this.getMessageApi.setAgentIndex(this.agentAlias);
        const isVA = !!this.sdkInst.isVirtualAgent();

        if (!this.getMessageApi.displayOpenerRichMessage(opener,
            isRestoring,
            isVA,
            this.agentAlias,
            openerts)) {
            const msg = new Message({
                bubbleType: BubbleType.OPENER_MESSAGE,
                messageText: opener,
                isAuto: isVA,
                name: this.agentAlias,
                timestamp: openerts,
            });
            msg.setRestore(isRestoring);
            this.addToUI(msg);
        }
    }

    /**
     * Async chat opener logic
     * - if the last conversation is not active and if showOpenerForEndedConv property is true then display opener,if false then don't
     * display opener but add the opener to engage params so that agent sees the opener
     * @return {Promise<any>|Promise<void>}
     */
    setAsyncOpenerStatus(userMovedToAuthenticated = false) {
        if (!this.isLastConvActive) {
            this.addHeaderToBottom();
            this.setDisclaimerText(false);
            return this.getOpener(true);
        }

        if (!this.isRestoring && !userMovedToAuthenticated && this.isLastConvActive) {
            this.setDisclaimerText(false);
        }
        return Promise.resolve();
    }

    agentResolved() {
        this.isLastConvActive = false;
        this.getOpener(false);
    }

    /**
     * Letting SDKInst knows the chat is displayed
     * @param {Boolean} reInitialize - If customer allowed to create a new engagement
     * after agent left from same chat window
     */
    onFrameViewReady(reInitializeCallbacks) {
        const that = this;
        this.customerName = this.getPersistedValue(Constants.CUSTOMER_DISPLAY_NAME, this.customerName);
        this.sdkInst.chatDisplayed({
            customerName: that.customerName,
            disconnectCb: that.onNetworkDisconnected.bind(that),
            reConnectCb: that.onNetworkReconnected.bind(that),
            failedCb: that.onNetworkFailed.bind(that),
            previousMessagesCb: that.onPreviousMessages.bind(that),
            defaultAgentAlias: that.agentAlias,
            skinConfig: that.uiConfig.skinConfig,
            autoConnectedCb: that.onAutoConnected.bind(that),
        }, reInitializeCallbacks);
    }

    /**
     * we need to pass callback for various events when reinitied
     */
    reInitCIAPI() {
        this.onFrameViewReady(true);
    }

    /**
     * fires when network is disconnected
     *  @callback
     */
    onNetworkDisconnected() {
        if (!Timeout.exists(Constants.NETWORK_MSG_TIMER)) {
            Timeout.set(Constants.NETWORK_MSG_TIMER, () => {
                this.getMessageApi.addSystemMessage(this.networkDisconnected, SYSTEM_STATE.NETWORK_DISCON, true);
            }, this.networkMessageDelay);
        }
    }

    /**
     * fires when network is reconnected
     *  @callback
     */
    onNetworkReconnected() {
        if (Timeout.exists(Constants.NETWORK_MSG_TIMER) && Timeout.executed(Constants.NETWORK_MSG_TIMER)) {
            this.getMessageApi.addSystemMessage(this.networkConnected, SYSTEM_STATE.NETWORK_RECON, true);
        }

        Timeout.clear(Constants.NETWORK_MSG_TIMER);
    }

    /**
     * fires when chat connection is lost
     *  @callback
     */
    onNetworkFailed() {
        this.getMessageApi.addSystemMessage(this.networkFailed, SYSTEM_STATE.NETWORK_FAILED);
        this.getMessageApi.updateMessagingTopBarStatus(STATUS_STATE.DISCONNECT);
        this.disableFooter(true);
    }

    /**
     * fires with all previous Messages and when async reconnect happens
     *  @callback
     *  @param {Object} responseObj
     */
    onPreviousMessages(responseObj) {
        // should not render messages when async reconnect as it is already availble in getTranscript
        if (this.isAsyncReconnect) {
            responseObj = this.returnNonDuplicateMessagesInAsyncReconnect(responseObj);
            this.isAsyncReconnect = false;
        }

        if (responseObj) {
            this.getMessageApi.setWindowId(responseObj.windowId);
            const count = responseObj.messages.length - 1;
            responseObj.messages.forEach((message, index) => {
                this.getMessageApi.onNewMessage(message, true, index == count);
            });
        }

        this.isRestoring = false;        

        /**
         * queries sdkInstance for incomming new messages after the reload
         */
        this.sdkInst.getMessages(msgObj => {
            this.getMessageApi.onNewMessage(msgObj);
        });
    }

    returnNonDuplicateMessagesInAsyncReconnect(response) {
        const messagesInTranscript = this.getRenderingEngine().getMessages();

        let responseFiltered = response;
        responseFiltered.messages = response.messages.filter(newMessage => {
            const { messageText:newText, messageTimestamp:newTs } = newMessage.data;
        
            return !messagesInTranscript.some(oldMessage => {
                const { text:oldText, _timestamp:oldTs } = oldMessage;
                return oldText === newText && oldTs === newTs;
            });
        });
        return responseFiltered;
    }

    /**
     * logic to render Async Transcript as and when it is available.
     * auto engagement or customer initiated engagement shouldn't be
     * blocked while conversation Transcript is fetched.
     * Query sdkInstance for previous Async Transcripts.
     */
    getPrevConversation(ignoreSurvey, userMovedToAuthenticated = false) {
        let preChatParam;
        const restore = this.isRestoring;

        this.lastHasMessages = false;

        if (this.waitForOpener) {
            this.disableFooter(false);
        }

        const requestTime = Date.now();

        // Make a prallel request to fetch async transcript
        const asyncPromise = functionToPromiseWrapper(this.sdkInst.getPreviousAsyncTranscript);
        this.isLastConvActive = this.sdkInst.isLastConversationActive();
        functionToPromiseWrapper(cb => {
            if (this.isPreChatSurveyNeed() && !ignoreSurvey) {
                if (!this.isLastConvActive) {
                    return this.shoutui
                        .getLayoutEl().renderSurvey().then(engageParams => {
                            if (engageParams) {
                                this.setupPreChatParams(engageParams);
                                preChatParam = engageParams;
                            }

                            cb();
                        });
                } else { // eslint-disable-line no-else-return
                    this.shoutui.getLayoutEl().setSurveyVisibility(false);
                }
            }

            if (this.isLastConvActive && this.getPersistedValue(Constants.CLIENT_OUT, -1) === 1) {
                this.clientOutCome = true;
            }

            cb();
        }).then(() => this.setAsyncOpenerStatus(userMovedToAuthenticated)).then(() => {
            if (this.waitForOpener) {
                this.enableFooter();
            }

            this.onFrameViewReady(false);

            if (preChatParam) {
                Timeout.instantiate(() => {
                    this.autoEngage(preChatParam.agentOutcome, preChatParam.data);
                }, 250);
            } else if (this.startAutoEngagement && !this.isRestoring) {
                this.autoEngage(this.autoEngageText);
            }

            const loadingMsg = new Message({
                bubbleType: BubbleType.SYSTEM_MESSAGE,
                messageText: this.historyLoadingText,
            });
            loadingMsg.setInternalState(SYSTEM_STATE.LOADING_MSG);

            this.getRenderingEngine().toggleLoadingMsg(loadingMsg, true);

            // return the async promise, then callback will be executed when promise resolves.
            return asyncPromise;
        }).then(this.processAsyncTranscript.bind(this, restore, requestTime));
    }

    /**
     * start rendering async Transcript to the top of chat window
     * @param {Array} response - conversation history
     * @param {Data} startTime - timestamp represending the start of request
     * @param {boolean} restore - is rendering during restoring
     */
    processAsyncTranscript(restore, startTime, response) {
        const injectDely = Date.now() - startTime < 2000;

        /*
         * if getTranscript request comes very quickly then make sure the there
         * is enough time to display and hide the loading message
         */
        Timeout.set("asyncTimer", () => {
            if (response && response.length > 0) {
                this.asyncPrev = true;
                this.conversations = response;
                const lastMsgs = stripHeaderFromTranscript(response[response.length - 1].messages);
                this.lastHasMessages = lastMsgs.length > 0;
                this.getMessageApi.lastMsgTS = this.lastHasMessages ? parseInt(lastMsgs[lastMsgs.length - 1].timestamp, 10) : 0;

                if (restore && !this.lastHasMessages) {
                    /*
                     *  if the current active conversation has empty messages and
                     *  if restoring fetch opener to display at the top of chat window
                     */

                    if (this.conversations[0][KEY_TYPE.KEY_CONVERSATION_ID]) {
                        this.recentConversationID = this.conversations[0][KEY_TYPE.KEY_CONVERSATION_ID];
                    }

                    this.setOpenerToOngoingAsyncConversation();
                } else {
                    this.processAsyncRendering();
                    this.asyncPrev = false;
                    this.getRenderingEngine().toggleLoadingMsg(null, false);
                }
            }
        }, injectDely ? 1000 : 25);
    }

    setOpenerToOngoingAsyncConversation() {
        this.conIndex = this.conversations.length - 1;
        return this.getOpener(true).then(() => {
            this.setDisclaimerText(true);
            this.addHeaderToBottom();
            this.asyncPrev = false;
            this.getRenderingEngine().toggleLoadingMsg(null, false);
        });
    }

    processAsyncRendering() {
        if (this.asyncMode.toLowerCase() === Constants.ASYNC_LAST) {
            // render Only last conversation
            this.conIndex = 0;
            this.renderAsyncConversation(this.conversations[0]);
        } else {
            this.conIndex = this.conversations.length - 1;
            const con = this.conversations[this.conIndex];
            this.renderAsyncConversation(con);
            this.addHeader(con);
        }

        this.recentConversationID = this.conversations[this.conIndex][KEY_TYPE.KEY_CONVERSATION_ID];
    }

    addSurveyToTranscript(conversationObj) {
        if (conversationObj.surveyMessage) {
            const alias = conversationObj.surveyAlias;

            const message = new Message({
                messageText: com.inq.flash.client.chatskins.SkinControl.prepareAsyncSurveyMessage(
                    conversationObj.surveyMessage,
                    conversationObj.conversationID
                ),
                name: alias,
                timestamp: this.getMessageApi.lastMsgTS || Date.now(), // eslint-disable-line object-shorthand
            });
            message.setAgentIndex(this.getMessageApi.setAgentIndex(alias));
            message.setRestore(true);
            this.renderEngine.addNewItemToTop(message);
        }
    }

    /**
     * Creates a header bubble and add it to the rendering engine to display it
     * @param {object} conversation
     */

    addHeader(conversation) {
        if (this.showConvHeader) {
            const head = new Message({ bubbleType: BubbleType.CONVERSATION_HEADER });
            let ts = Date.now();

            if (conversation.messages.length > 0) {
                ts = conversation.messages[0][KEY_TYPE.KEY_TIMESTAMP];
            }

            head.setMessageText(String(ts));
            head.setTimeStamp(ts);
            head.setRestore(true);
            this.renderEngine.addNewItemToTop(head);
        }
    }

    /**
     * adding conversation header when last conversation is resolved or when getTranscript return zero message for current Active conversation.
     * For current Active conversation it uses opener time stamp and for header when resolved it uses current time stamp.
     */
    addHeaderToBottom() {
        if (this.isHeaderNeeded()) {
            const timestamp = parseInt(this.getPersistedValue(Constants.OPENERTS,
                Date.now()), 10) || Date.now();
            this.addToUI(new Message({
                bubbleType: BubbleType.CONVERSATION_HEADER,
                messageText: String(timestamp),
                timestamp: timestamp,
            }));
        }
    }

    isHeaderNeeded() {
        return this.isAsyncChat && this.asyncMode.toLowerCase() !== Constants.ASYNC_LAST;
    }

    /**
     * enables lazy loading of all async conversation, as user scrolls up previous conversation are injected to the transcript
     */
    lazyloadConversation() {
        if (this.conversations && this.conIndex > 0) {
            this.lazyloading = true;
            this.conIndex -= 1;
            const con = this.conversations[this.conIndex];
            this.renderAsyncConversation(con);
            this.addHeader(con);
        }
    }

    /**
     * @return {boolean}
     */
    hasMoreConversations() {
        return this.conIndex > 0;
    }

    /**
     * render messages in a conversation
     * @param {object} conversationObj
     */

    renderAsyncConversation(conversationObj) {
        this.addSurveyToTranscript(conversationObj);

        if (conversationObj.messages && conversationObj.messages.length > 0) {
            for (let i = conversationObj.messages.length - 1; i >= 0; i--) {
                const msgObj = conversationObj.messages[i];
                let message;
                const timestamp = msgObj[KEY_TYPE.KEY_TIMESTAMP];
                const text = msgObj[KEY_TYPE.KEY_MSG_TEXT];
                const islast = (i == 0 && (this.conIndex == this.conversations.length - 1) && this.isLastConvActive);

                if (msgObj.messageType === MessageType.TYPE_CHATLINE || msgObj.messageType === MessageType.TYPE_CHAT_COMMUNICATION_SURVEY) {
                    const aID = msgObj[KEY_TYPE.KEY_AGENT_ID];
                    const alias = msgObj[KEY_TYPE.KEY_MSG_AGENT_ALIAS];

                    if (aID != null || alias != null) {
                        const rData = this.getDataMessage(msgObj);

                        if (rData && this.getMessageApi.displayDataMessage(rData, text, true, false, alias || aID,
                            timestamp, islast,
                            false, true)) {
                            // eslint-disable-line no-empty
                        } else {
                            message = new Message({
                                messageText: text,
                                name: alias,
                                timestamp: timestamp, // eslint-disable-line object-shorthand
                            });

                            if (aID && aID.startsWith("virtualAssistant")) {
                                message.setVA();
                                message.setMessageBubble(BubbleType.VIRTUAL_AGENT_MESSAGE);
                            } else if (aID && aID.toLowerCase().startsWith("opener")) {
                                message.setMessageBubble(BubbleType.OPENER_MESSAGE);
                            }

                            message.setAgentIndex(this.getMessageApi.setAgentIndex(alias || aID));
                        }
                    } else {
                        const calias = msgObj[KEY_TYPE.KEY_MSG_CUSTOMER_ALIAS] || this.customerName;

                        if (text.startsWith(Constants.EA)) {
                            const rData = this.getDataMessage(msgObj);

                            if (rData) {
                                this.getMessageApi.displayDataMessage(rData, text, true, false, calias, timestamp, false,
                                    true, true);
                            }
                        } else {
                            message = new Message({
                                bubbleType: BubbleType.CUSTOMER_MESSAGE,
                                messageText: text,
                                name: calias,
                                timestamp: timestamp, // eslint-disable-line object-shorthand
                            });
                        }
                    }
                } else if (msgObj.messageType === MessageType.TYPE_CHAT_AUTOMATON_REQUEST) {
                    const alias = msgObj[KEY_TYPE.KEY_MSG_AGENT_ALIAS];
                    const rData = this.getDataMessage(msgObj);

                    if (rData && this.getMessageApi.displayDataMessage(rData, text, true, true, alias, timestamp,
                        islast, false, true)) {
                        // eslint-disable-line no-empty
                    } else {
                        message = new Message({
                            bubbleType: BubbleType.VIRTUAL_AGENT_MESSAGE,
                            messageText: text,
                            name: alias,
                            isAuto: true,
                            timestamp: timestamp, // eslint-disable-line object-shorthand
                        });
                        message.setAgentIndex(this.getMessageApi.setAgentIndex(alias));
                    }
                }

                if (message) {
                    message.setRestore(true);
                    this.renderEngine.addNewItemToTop(message);
                }
            }
        }
    }

    getDataMessage(msgObj) { // eslint-disable-line class-methods-use-this
        const msgData = msgObj[KEY_TYPE.KEY_MESSAGE_DATA];

        if (msgObj[KEY_TYPE.KEY_MSG_TEXT]) {
            const msgText = msgObj[KEY_TYPE.KEY_MSG_TEXT];

            try {
                const richData = JSON.parse(decodeEntities(msgText.trim()));

                if (isObject(richData)) {
                    return (richData.richWidget || richData);
                }
            } catch (e:any) {
                return null;
            }
        }

        if (msgData != null) {
            const richData = JSON.parse(msgData);
            return (richData.richWidget ? richData.richWidget : richData);
        }
        return null;
    }

    /**
     * Fires when an engagement is created
     * @callback
     */
    onEngageStarted() {
        this.sdkInst.getMessages(msgObj => {
            this.getMessageApi.onNewMessage(msgObj);
        });

        this.stateMgr.setState({ state: CHAT_STATE.ENGAGED });
    }

    onAutoConnected() {
        this.onEngageStarted();
    }

    /**
     * Fires when an engagement creation is failed
     * @callback
     */
    onEngageFailed() {
        this.getMessageApi.addSystemMessage(this.serviceNotAvailable, SYSTEM_STATE.DENIED_MSG);
        this.disableFooter(true);
    }

    /**
     * called when a new customer enters a new message
     * @callback
     * @param {string} text
     */
    onNewCustomerBubble(text) {
        if (this.sdkInst.isChatInProgress()) {
            this.sdkInst.sendMessage(text);
        } else {
            /**
             * clear any pending openers from displaying
             */
            this.clearOpenerTimer();

            functionToPromiseWrapper(this.sdkInst.engageChat, text).then(this.onEngageResponse
                .bind(this));
        }

        this.addCustomerBubble(text);
    }

    /**
     * handle engagement status
     * @param {int} httpStatus
     * @param {string} statusType
     * @param {string} windowId
     */
    onEngageResponse({ httpStatus, statusType, asyncReconnect, windowId }) {
        if (httpStatus === 200 && statusType === "complete") {
            this.isAsyncReconnect = asyncReconnect;
            this.onEngageStarted();
            this.getMessageApi.setWindowId(windowId);
        } else {
            this.onEngageFailed();
        }
    }

    clearOpenerTimer(executeOpenerCallback = false) {
        if (this.openerTimers && this.openerTimers.length > 0) {
            this.openerTimers.forEach(timer => {
                const func = timer.clear();
                if (executeOpenerCallback && typeof func === "function") {
                    func();
                }
            });
            const notShown = this.openerTimers.length - this.openerDisplayIndex;

            if (notShown != 0) {
                this.persistValue(Constants.OPENERDISPLAY, this.openerDisplayIndex);
            }
        }
    }

    sendRichMediaData(text, data) {
        this.displayRecieptResponse(text, data);

        if (text && !text.startsWith(Constants.EA)) {
            this.addCustomerBubble(text, undefined, undefined, true);

            if (!data.receipt) {
                this.sdkInst.sendRichContentMessage(text, data, this.onEngageResponse.bind(this));
            }
        }
    }

    sendResponseLinkData(text, data) {
        if (text && !text.startsWith(Constants.EA)) {
            this.addCustomerBubble(text);
        }
        this.sdkInst.sendRichContentMessage(text, data, this.onEngageResponse.bind(this));
    }

    displayRecieptResponse(text, data) {
        if (data.receipt) {
            data.receipt.constants = data.constants;
            delete data.constants;
            this.getMessageApi.displayDataMessage(data, text, false, false, this.customerName, Date.now(), false, true);
        }
    }

    /**
     *
     * @param {CALL_MESSAGE_TYPE} type
     */
    sendWebRTCInvitationState(type) {
        this.sdkInst.sendRichContentMessage(Constants.EA, { webRTCEvent: { name: type } });
        this.persistValue(Constants.CALL_STATE, CALL_STATE.CALL_INVITED);
    }

    /**
     * start a engagement automatically, application can decide if auto engage is needed
     * @param {string} text
     * @param {Object} engageParams
     */
    autoEngage(text, engageParams?) {
        this.clearOpenerTimer(true);
        functionToPromiseWrapper(this.sdkInst.autoEngage, text, engageParams).then(this.onEngageResponse.bind(this));
    }

    /**
     * creates customer message bubble and invokes rendering engine to process it
     * @param {string} text
     * @param restore
     * @param {integer} timestamp
     */
    addCustomerBubble(text, restore?, timestamp?, richMediaDisplayText?) {
        const msg = new Message({
            bubbleType: BubbleType.CUSTOMER_MESSAGE,
            messageText: text,
            isAuto: false,
            name: this.customerName,
            timestamp: timestamp,
            richMediaDisplay: richMediaDisplayText
        });
        msg.setRestore(restore);
        this.addToUI(msg);

        this.showTypingIndicatorForVA();
    }

    showTypingIndicator(msgText) {
        const msg = new Message({
            bubbleType: BubbleType.TYPING_MESSAGE,
            messageText: msgText,
        });
        this.addToUI(msg);
    }

    showTypingIndicatorForVA() {
        if (this.vaTyping !== 0 && this.sdkInst.isVirtualAgent()) {
            Timeout.set(Constants.VA_TYPING_TIMER, () => {
                this.showTypingIndicator(this.typingMsg);
            }, this.vaTyping);
        }
    }

    /**
     * Handles VA link click event, sends the event to SDKInstance to process it
     * @param {Event} event
     */
    onVALinkClick(event) {
        this.getSDKInstance().sendVALinkMessage(event, text => {
            this.addCustomerBubble(text, false);
            pubSubGlobal.publish("vaDialog.clicked", event);
        }, linkEl => {
            /**
             * pass the link click to FrameBridge, since the chat window is within sandboxed iframe , top window navigation must be
             * passed to Bootstrap
             */
            this.bridge.pushToFrameSet(linkEl.href, linkEl.target);
        }, this.onEngageResponse.bind(this));
    }

    getRestoring() {
        return this.isRestoring || this.asyncPrev;
    }

    /**
     * Forcing rendering engine to render the give message Object.
     * if this is called during previous conversation transcript rendering
     * then add the message to top instead of bottom
     * @param {Message} message
     */
    addToUI(message) {
        if (message) {
            if (this.asyncPrev) {
                this.renderEngine.addNewItemToTop(message);
            } else {
                this.renderEngine.addNewItem(message);
            }
        }
    }

    updateLastMessage(message) {
        const msgs = this.renderEngine.getMessages();
        const len = msgs.length - 1;

        if (msgs[len].getRichData() && msgs[len].getRichData().widgetView == RICH_WIDGET.INLINE) {
            this.renderEngine.update(len, message);
        } else {
            this.addToUI(message);
        }
    }

    getSDKInstance() {
        return this.sdkInst;
    }

    getUIConfig() {
        return this.uiConfig;
    }

    getImagePath() {
        return this.sdkInst.getImagePath();
    }

    getIsMinimized() {
        return this.getStateMgr().state.isMinimized;
    }

    getBridge() {
        return this.bridge;
    }

    getRenderingEngine() {
        return this.renderEngine;
    }

    /**
     * Will handle the display of close confirmation model , only if its turned on and chat is in progress
     */
    closeChat() {
        const layout = this.shoutui.getLayoutEl();

        if (!this.isChatDone && this.uiConfig.appConfigProps.closeConfirmation && this.sdkInst.isChatInProgress()) {
            layout.showCloseModal();
        } else {
            this.stateMgr.setState({ state: CHAT_STATE.ENDED });
            layout.invokeSurveyClose();
            this.sdkInst.closeChat();
        }
    }

    setChatDoneState(val) {
        this.isChatDone = val;
    }

    setSoundState(state) {
        this.persistValue(Constants.SOUND_STATE, state ? 1 : 0);
    }

    persistValue(key, value) {
        this.sdkInst.setPersistedData(key, value);
    }

    removePersistedValueByKey(key) {
        this.sdkInst.removePersistedDataByKey(key);
    }

    getPersistedValue(key, defaultVal) {
        return this.sdkInst.getPersistedData(key, defaultVal);
    }

    /**
     * Sets states incomming from frame-bridge to the FrameBridge state object properties.
     * Will only accept existing property names defined on StateMgr init (throws an error if it does not)
     *
     * Currently expected:
     * FrameBridge = {
     *   readyFocus,     <- signals control focus required, it's value should be the target control name/id
     *   setCount        <- counter used to force a state change even if the value did not change (auto-increments)
     * }
     *
     * @param {String} stateName - An "existing" property name in the StateMgr state object "FrameBridge"
     * @param {any} value - The value to be set
     * @param {Boolean} forceStateChange - Force a state change event to trigger even if value has not changed
     */
    setFrameBridgeState(stateName, value, forceStateChange) {
        this.stateMgr.setStateItem(`FrameBridge.${stateName}`, value);

        if (forceStateChange) {
            const setCount = this.stateMgr.state.FrameBridge.setCount + 1;
            this.stateMgr.setStateItem("FrameBridge.setCount", setCount);
        }
    }

    isSoundEnabled() {
        const defaultVal = (this.audioInitialSetting === TOGGLE_SOUND.SOUND_ON) ? 1 : 0;
        return parseInt(this.getPersistedValue(Constants.SOUND_STATE,
            defaultVal), 10) == 1;
    }

    isEmailRequested() {
        return parseInt(this.getPersistedValue(Constants.EMAIL_REQ, -1), 10) == 1;
    }

    minimize() {
        this.onMinimizeFrame();
    }

    updateLogo(logoObj) {
        this.shoutui.updateLogo(logoObj);
    }

    updateTitle(title) {
        this.shoutui.updateTitle(title);
    }

    disableFooter(ended) {
        this.isChatDone = ended;

        if (ended) {
            this.stateMgr.setState({ state: CHAT_STATE.ENDED });
        }

        this.shoutui.getFooter().disable();
    }

    enableFooter() {
        this.shoutui.getFooter().enableFooter();
    }

    disableEmailButtonWhenAsync() {
        this.shoutui.disableEmail();
    }

    scrollTranscriptToBottom() {
        this.shoutui.getTranscriptEl().scrollBottom();
    }

    displayPostChatSurveyUponChatload() {
        const data = this.sdkInst.getPostChatSurveyData();
        const { surveyID } = data;
        delete data.surveyID;
        delete data.surveyChatSpecJSON;
        this.displayPostChatSurvey(surveyID, data, null);
    }

    displayPostChatSurvey(pcsID, data, asyncData) {
        this.shoutui.resetTopBar();
        this.shoutui.getLayoutEl().renderPostChatSurvey(pcsID, data, asyncData);
    }

    toggleFullScreen(fullScreen: any): void {
        this.shoutui.updateFullScreen(fullScreen);
    }

    getStateMgr() {
        return this.stateMgr;
    }

    /**
     * Handles close and minimize with accessKeys
     * mainKey is used as first combination key which is same for close and minimize
     * and allowed keys are "altKey", "ctrlKey", "shiftKey", "metaKey"
     * second combination key (closeKey or minimizeKey) is any single digit between 0 to 9
     * and second key stores corresponding keyCode for ex. digit 2 has 50 keyCode value
     * @param event
     */
    handleAccessKeyPress(event) {
        const ALLOWED_KEYS = ["altKey", "ctrlKey", "shiftKey", "metaKey"];

        if (!this.hasCloseConfirmModalVisible()
            && ALLOWED_KEYS.includes(this.minimizeAndCloseAccessKeys.mainKey)
            && event[this.minimizeAndCloseAccessKeys.mainKey]) {
            if (event.keyCode === this.minimizeAndCloseAccessKeys.closeKey) {
                this.closeChat();
            } else if (event.keyCode === this.minimizeAndCloseAccessKeys.minimizeKey) {
                this.minimize();
            }
        }
    }

    /**
     * Checks visibility of close modal on Chat screen
     * @returns {boolean} isCloseConfirmVisible
     */
    hasCloseConfirmModalVisible() {
        return this.stateMgr.state.isCloseConfirmVisible;
    }

    /**
     * Create an instance of RichMediaReporting class
     * @return {RichMediaReporting}
     */
    getRichMediaReporter() {
        if (!this.richReporter) {
            this.richReporter = new RichMediaReporting(this);
        }
        return this.richReporter;
    }

    /**
     * This method is to check if video call in progress
     * @returns {boolean} boolean
     */
    isCallInProgress() {
        const state = this.stateMgr.state.state;

        if (state === CHAT_STATE.PRECALL || state === CHAT_STATE.INCALL) {
            return true;
        }
        return false;
    }

    /**
     * This method returns conversation of async chat
     * @returns {number} number
     */
    getLatestConversationID() {
        return this.recentConversationID;
    }

    /**
     * This method returns enagement id for  chat coversation
     * @returns {number} number
     */
    getEngagementID() {
        return this.engagementID;
    }

    /**
     * This method sets engagement id for chat coversation
     * @param {number} engagementID engagement id
     */
    setEngagementID(engagementID) {
        this.engagementID = engagementID;
    }

    /**
     * This method sets agent id of this chat
     * @param {number} agentID agent id
     */
    setAgentID(agentID) {
        this.agentID = agentID;
    }


    setAsyncChat(isAsync:boolean = true) {
        this.isAsyncChat = isAsync;
    }


    /**
     * This method sets conversation id of Async coversation
     * @param {number} conversationId conversation id
     */
    setConversationID(conversationId) {
        this.conversationID = conversationId;
    }

    /**
     * check if chat should be minimized upon page reload
     * @param {object} minimized - current minimized state
     * @return {boolean}
     */
    checkAndMinimizeOnNewPageLoad(minimized) {
        const deviceType = this.sdkInst.getDeviceType();
        let minimize = false;

        if ((minimized && minimized.state)
            || !this.sdkInst.isChatInProgress()) {
            return minimize;
        }

        switch (this.minimizeOnNewPage) {
            case NEW_PAGE_MINIMIZED.ALL:
                minimize = true;
                break;

            case NEW_PAGE_MINIMIZED.MOBILE:
                if (deviceType === "Phone" || deviceType === "Tablet") {
                    minimize = true;
                }

                break;

            case NEW_PAGE_MINIMIZED.PHONE:
                if (deviceType === "Phone") {
                    minimize = true;
                }
        }

        /**
         * mc is a hardcoded value for minimized state in cookie,
         * 0 means minimized and -1 means not minimized
         */
        if (minimize) {
            this.persistValue(Constants.MINIMIZED_COUNT, 0);
        }
        return minimize;
    }

    isChatEngaged() {
        return this.sdkInst.isContinued()
            && (this.getPersistedValue(Constants.MESSAGE_COUNT, 0) > 0
                || this.getPersistedValue(Constants.CHAT_ENGAGED, false)
            );
    }

    /**
     * This method logs web rtc events with chat params
     * @param {string} eventType - current event for which log executed
     * @param {string} callType - DVV type
     */
    logWebRTCEvents(eventType: string, callType: string): void {
        const chatParams = this.getSDKInstance().getChatParams();
        this.getSDKInstance().logCIDVVEvent({
            siteID: chatParams.siteID,
            params: {
                agentID: this.agentID,
                chatID: chatParams.getChatID(),
                customerID: chatParams.getCustomerID(),
                dvvType: callType,
            },
        }, eventType);
    }
}

// window.CIApplication = CIApp;
